import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Agent } from "../../model_gen/agent_base";
import { Application } from "../../model_gen/application";
import { AgentService } from "../../services/AgentService";
import { ApplicationService } from "../../services/ApplicationService";
import AgentSessionsTable from "./AgentSessionsTable";
import AgentEditModal from "./AgentEditModal";
import AgentCapabilitiesGraph from "./AgentCapabilitiesGraph";
import AddCapabilityModal from "./AddCapabilityModal";
import AgentCapabilitiesTable from "./AgentCapabilitiesTable";
import FormFieldModal from "./FormFieldModal";
import AgentFormFieldsTable from "./AgentFormFieldsTable";
import AgentRoutingConfig from "./AgentRoutingConfig";
import AgentVersionsTable from "./AgentVersionsTable";
import { VersionBadge } from "./VersionBadge";
import AgentLiveActivity from "./AgentLiveActivity";
import RequestAnalyticsChart from "../shared/RequestAnalyticsChart";
import {
  AnalyticsService,
  RequestAnalyticsData,
} from "../../services/AnalyticsService";
import { QuickPeriod } from "../shared/AnalyticsDateFilter";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Tooltip } from "../ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import PromptModal from "../ui/PromptModal";
import {
  PlusIcon,
  SettingsIcon,
  UserIcon,
  HistoryIcon,
  EditIcon,
  CheckIcon,
  UploadIcon,
  Info,
  XIcon,
  FileText,
  Loader2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";
import { toast } from "sonner";

interface TabItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const AgentOverview: React.FC = () => {
  const { apiKey, companyId } = usePermissions();
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version") || undefined;

  const [agent, setAgent] = useState<Agent | null>(null);
  const [application, setApplication] = useState<Application | null>(null);
  const [loadingState, setLoadingState] = useState<
    "loading" | "loaded" | "error"
  >("loading");

  // Get parent chain from URL params and fetch agent names
  const parentsChain = searchParams.get("parents");
  const [breadcrumbAgents, setBreadcrumbAgents] = useState<Agent[]>([]);
  const [breadcrumbsLoading, setBreadcrumbsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [configSubTab, setConfigSubTab] = useState<string>("routing");

  // New state for tracking capabilities
  const [hasActions, setHasActions] = useState<boolean>(false);
  const [hasSubAgents, setHasSubAgents] = useState<boolean>(false);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState<boolean>(true);

  // Edit mode state
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedName, setEditedName] = useState<string>("");
  const [editedDescription, setEditedDescription] = useState<string>("");
  const [editedBaseUrl, setEditedBaseUrl] = useState<string>("");
  const [editedStrategyAddendum, setEditedStrategyAddendum] =
    useState<string>("");
  const [baseUrlError, setBaseUrlError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const closeAllChildrenRef = useRef<(() => void) | null>(null);
  const [hasExpandedChildren, setHasExpandedChildren] =
    useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [addCapabilityOpen, setAddCapabilityOpen] = useState<boolean>(false);
  const [capabilitiesRefreshKey, setCapabilitiesRefreshKey] =
    useState<number>(0);
  // Versioning state
  const [latestVersion, setLatestVersion] = useState<string | undefined>(undefined);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState<boolean>(false);
  const [publishModalOpen, setPublishModalOpen] = useState<boolean>(false);
  const [publishIncrement, setPublishIncrement] = useState<"major" | "minor" | "patch">("minor");
  const [publishNotes, setPublishNotes] = useState<string>("");
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isDraftingFromVersion, setIsDraftingFromVersion] = useState<boolean>(false);
  const [draftConfirmDialogOpen, setDraftConfirmDialogOpen] = useState<boolean>(false);
  const [pendingDraftVersion, setPendingDraftVersion] = useState<string | null>(null);
  const [saveConfirmDialogOpen, setSaveConfirmDialogOpen] = useState<boolean>(false);
  const [pendingSaveAction, setPendingSaveAction] = useState<(() => void) | null>(null);
  const isVersionView = !!version;

  // Field action confirmation dialogs
  const [fieldMoveDialogOpen, setFieldMoveDialogOpen] = useState<boolean>(false);
  const [fieldDeleteDialogOpen, setFieldDeleteDialogOpen] = useState<boolean>(false);
  const [pendingFieldAction, setPendingFieldAction] = useState<{
    type: "move" | "bulkMove" | "delete";
    fieldNames?: string[];
    fieldIndex?: number;
    targetAgentId?: number;
    targetAgentName?: string;
  } | null>(null);

  // Agent Form editor state
  const [formFields, setFormFields] = useState<
    Array<{
      name: string;
      description: string;
      type: string;
      updateRules?: string;
      readScope: string;
      writeScope: string;
      readAgents?: number[];
      writeAgents?: number[];
    }>
  >([]);
  const [formDirty, setFormDirty] = useState<boolean>(false);
  const [formSaving, setFormSaving] = useState<boolean>(false);
  const [agentFormModalOpen, setAgentFormModalOpen] = useState<boolean>(false);
  const [formSearchQuery, setFormSearchQuery] = useState<string>("");
  const [formSortBy, setFormSortBy] = useState<"name" | "type" | "none">(
    "none"
  );
  const [formSortDir, setFormSortDir] = useState<"asc" | "desc">("asc");
  // Special Instructions (Strategy Notes)
  const NOTE_BULLET_PREFIX = "- ";
  const NOTE_BULLET_REGEX = /^\s*-\s+/;
  const [specialInstructions, setSpecialInstructions] = useState<string[]>([]);
  const [notesDirty, setNotesDirty] = useState<boolean>(false);
  const [notesSaving, setNotesSaving] = useState<boolean>(false);
  const [specialInstructionsModalOpen, setSpecialInstructionsModalOpen] =
    useState<boolean>(false);
  const [capabilitiesSubTab, setCapabilitiesSubTab] = useState<string>("table");
  const [instructionsSubTab, setInstructionsSubTab] =
    useState<string>("instructions");
  // Structured Instructions (orchestration/strategy/processing)
  const [orchestrationInstructions, setOrchestrationInstructions] =
    useState<string>("");
  const [strategyInstructions, setStrategyInstructions] = useState<string>("");
  const [processingInstructions, setProcessingInstructions] =
    useState<string>("");
  const [instructionsDirty, setInstructionsDirty] = useState<boolean>(false);
  const [instructionsSaving, setInstructionsSaving] = useState<boolean>(false);
  const [instructionsInitialized, setInstructionsInitialized] = useState<boolean>(false);
  const [notesInitialized, setNotesInitialized] = useState<boolean>(false);

  // Prompt modal states
  const [orchestrationModalOpen, setOrchestrationModalOpen] =
    useState<boolean>(false);
  const [strategyModalOpen, setStrategyModalOpen] = useState<boolean>(false);
  const [processingModalOpen, setProcessingModalOpen] =
    useState<boolean>(false);

  // Modal states for form fields
  const [formFieldModalOpen, setFormFieldModalOpen] = useState<boolean>(false);
  const [editingFormFieldIndex, setEditingFormFieldIndex] = useState<
    number | null
  >(null);

  // Modal states for special instructions
  const [instructionModalOpen, setInstructionModalOpen] =
    useState<boolean>(false);
  const [editingInstructionIndex, setEditingInstructionIndex] = useState<
    number | null
  >(null);
  const [tempInstruction, setTempInstruction] = useState<string>("");

  // Helpers for parsing/saving Special Instructions while preserving rich markdown blocks
  const parseStrategyAddendumToNotes = (strategyRaw: string): string[] => {
    const raw = (strategyRaw || "").toString();
    const lines = raw.split("\n");
    const bulletLines = lines.filter((l) => NOTE_BULLET_REGEX.test(l));
    const startsWithHeaderBullet = /^\s*-\s*#/m.test(raw.trimStart());

    // If it's a header-style markdown starting with a dashed header, treat the whole thing as a single block
    if (startsWithHeaderBullet) {
      return [raw.trim()];
    }

    // If the majority of lines are dashed bullets, keep bullet semantics; otherwise treat as a single block
    if (
      bulletLines.length > 0 &&
      bulletLines.length >= Math.ceil(lines.length * 0.5)
    ) {
      return bulletLines
        .map((l) => l.replace(NOTE_BULLET_REGEX, "").trim())
        .filter((s) => s.length > 0);
    }

    const single = raw.trim();
    return single ? [single] : [];
  };

  const computeStrategyAddendumFromNotes = (notes: string[]): string => {
    const cleaned = (notes || [])
      .map((n) => n || "")
      .filter((n) => n.trim().length > 0);
    if (cleaned.length === 1 && /[\n#*`]/.test(cleaned[0])) {
      // Single rich block: save verbatim to preserve headings/lists/code blocks
      return cleaned[0].trim();
    }
    // Otherwise, serialize as dashed bullets (backwards compatible)
    return cleaned.map((n) => `${NOTE_BULLET_PREFIX}${n.trim()}`).join("\n");
  };

  // Pagination states
  const [formFieldsPage, setFormFieldsPage] = useState<number>(1);
  const [instructionsPage, setInstructionsPage] = useState<number>(1);
  const itemsPerPage = 10;

  // Derived, locally filtered + ordered form fields for the table view
  const visibleFormFields = useMemo(() => {
    const q = (formSearchQuery || "").trim().toLowerCase();
    let base = [...formFields];

    // Preserve original order if no sorting selected
    if (formSortBy === "none") {
      if (!q) {
        return base;
      }
      // Filter by query but preserve order
      base = base.filter((f) => {
        const name = (f.name || "").toLowerCase();
        const desc = (f.description || "").toLowerCase();
        return name.includes(q) || desc.includes(q);
      });
      return base;
    }

    // Apply sorting
    if (!q) {
      // No search: sort by selected column
      const getVal = (f: any) =>
        formSortBy === "name" ? f.name || "" : f.type || "";

      return base.sort((a, b) => {
        const va = getVal(a);
        const vb = getVal(b);
        if (va < vb) return formSortDir === "asc" ? -1 : 1;
        if (va > vb) return formSortDir === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      // Filter by query
      const getRank = (field: { name: string; description: string }) => {
        const name = (field.name || "").toLowerCase();
        const desc = (field.description || "").toLowerCase();
        if (name === q) return 0; // exact name
        if (desc === q) return 1; // exact desc
        if (name.startsWith(q)) return 2; // prefix name
        if (desc.startsWith(q)) return 3; // prefix desc
        if (name.includes(q)) return 4; // substring name
        if (desc.includes(q)) return 5; // substring desc
        return 9;
      };

      base = base.filter((f) => {
        const name = (f.name || "").toLowerCase();
        const desc = (f.description || "").toLowerCase();
        return name.includes(q) || desc.includes(q);
      });

      // When searching, sort by relevance rank, then by selected column
      const getVal = (f: any) =>
        formSortBy === "name" ? f.name || "" : f.type || "";

      base.sort((a, b) => {
        const ra = getRank(a);
        const rb = getRank(b);
        if (ra !== rb) return ra - rb;

        // Secondary sort by selected column
        const va = getVal(a);
        const vb = getVal(b);
        if (va < vb) return formSortDir === "asc" ? -1 : 1;
        if (va > vb) return formSortDir === "asc" ? 1 : -1;
        return 0;
      });

      return base;
    }
  }, [formFields, formSearchQuery, formSortBy, formSortDir]);

  // Reset pagination when search/sort changes
  useEffect(() => {
    setFormFieldsPage(1);
  }, [formSearchQuery, formSortBy, formSortDir]);

  const toggleFormSort = (column: "name" | "type") => {
    setFormSortBy((prev) => {
      if (prev === column) {
        // Cycle: asc -> desc -> none
        if (formSortDir === "asc") {
          setFormSortDir("desc");
          return prev;
        } else {
          // Was desc, go back to none (original order)
          setFormSortDir("asc");
          return "none";
        }
      }
      // New column selected, start with asc
      setFormSortDir("asc");
      return column;
    });
  };

  // Analytics state
  const [requestAnalyticsData, setRequestAnalyticsData] = useState<
    RequestAnalyticsData[]
  >([]);
  const [requestAnalyticsLoading, setRequestAnalyticsLoading] =
    useState<boolean>(false);
  const [requestAnalyticsPeriod, setRequestAnalyticsPeriod] =
    useState<QuickPeriod>({ label: "1W", days: 7 });
  const [requestAnalyticsPeriodDays, setRequestAnalyticsPeriodDays] =
    useState<number>(7);

  const pageSize = 10;

  const reloadAgent = useCallback(() => {
    if (!agentId) return;
    // Don't set loading states that cause full page refreshes - just update the data silently
    const detailed$ = isVersionView && version
      ? AgentService.getAgentVersion(Number(agentId), version, () => {}, apiKey)
      : AgentService.getAgentDetailed(Number(agentId), () => {}, apiKey, true); // Request capability versions

    detailed$.subscribe(
      (agentData: Agent) => {
        // If version view, agentData is the version payload: extract agent from "agent" field
        // Backend returns: { ...version_record_fields, "agent": restored_agent }
        const agent = isVersionView && (agentData as any)?.agent
          ? (agentData as any).agent
          : agentData;
        
        setAgent(agent as Agent);
        const actionsCount = (agent.actions || []).length;
        const subAgentsCount = (agent.sub_agents || []).length;
        setHasActions(actionsCount > 0);
        setHasSubAgents(subAgentsCount > 0);
        // Don't set capabilitiesLoading or loadingState here - just update the data silently
        if ((agent as any)?.application_id) {
          ApplicationService.getApplication(
            (agent as any).application_id,
            () => {},
            apiKey
          ).subscribe(
            (appData: Application) => setApplication(appData),
            (error: any) => console.error("Error loading application:", error)
          );
        }
      },
      (error: any) => {
        console.error("Error reloading agent:", error);
        setCapabilitiesLoading(false);
        setLoadingState("error"); // Set loading state to error on failure
      }
    );
  }, [agentId, apiKey, isVersionView, version]);

  // Use a ref to track if we're currently reloading to prevent infinite loops
  const isReloadingRef = useRef(false);

  // Listen for agent updates from assistant
  useEffect(() => {
    const handleAgentUpdate = (event: any) => {
      const affectedAgentIds = event.detail?.agentIds || [];

      // Don't reload if viewing a version (versions are immutable)
      if (isVersionView) {
        return;
      }

      // Reload if this agent was affected
      if (agentId && affectedAgentIds.includes(Number(agentId))) {
        // Prevent multiple simultaneous reloads
        if (isReloadingRef.current) {
          return;
        }

        isReloadingRef.current = true;

        AgentService.getAgentDetailed(
          Number(agentId),
          () => {}, // Don't change loading state to avoid triggering effects
          apiKey
        ).subscribe(
          (agentData: Agent) => {
            setAgent(agentData);
            const actionsCount = (agentData.actions || []).length;
            const subAgentsCount = (agentData.sub_agents || []).length;
            setHasActions(actionsCount > 0);
            setHasSubAgents(subAgentsCount > 0);

            // Reset the flag after a small delay to ensure state updates complete
            setTimeout(() => {
              isReloadingRef.current = false;
            }, 100);

            if (agentData.application_id) {
              ApplicationService.getApplication(
                agentData.application_id,
                () => {},
                apiKey
              ).subscribe(
                (appData: Application) => setApplication(appData),
                (error: any) =>
                  console.error("Error loading application:", error)
              );
            }
          },
          (error: any) => {
            console.error("❌ Error reloading agent:", error);
            isReloadingRef.current = false;
          }
        );
      }
    };

    window.addEventListener("agentUpdated", handleAgentUpdate);

    return () => {
      window.removeEventListener("agentUpdated", handleAgentUpdate);
    };
  }, [agentId, apiKey, isVersionView]);

  useEffect(() => {
    let agentSubscription: any = null;
    let applicationSubscription: any = null;
    let isCancelled = false; // Track if this effect has been cleaned up

    if (agentId) {
      // Clear state when loading new agent to prevent showing old data during fetch
      setAgent(null); // Clear agent state immediately when agentId changes
      setApplication(null); // Clear application state
      setFormFields([]);
      setSpecialInstructions([]);
      setOrchestrationInstructions("");
      setStrategyInstructions("");
      setProcessingInstructions("");
      setFormDirty(false);
      setNotesDirty(false);
      setInstructionsDirty(false);
      setLoadingState("loading"); // Set loading state immediately
      setCapabilitiesLoading(true);

      // Load agent details with actions and sub-agents in a single call
      const detailed$ = isVersionView && version
        ? AgentService.getAgentVersion(Number(agentId), version, (loading: boolean) => {
            if (loading) setLoadingState("loading");
          }, apiKey)
        : AgentService.getAgentDetailed(Number(agentId), (loading: boolean) => {
            if (loading) setLoadingState("loading");
          }, apiKey, true); // Request capability versions

      agentSubscription = detailed$.subscribe(
        (agentData: Agent) => {
          // If version view, use the restored agent directly (with actions from snapshot)
          // Otherwise use agentData directly
          const agent = isVersionView && (agentData as any)?.agent
            ? (agentData as any).agent  // Use restored agent with version-specific actions
            : agentData;
          
          // Ignore stale responses from previous agentId
          if (isCancelled) return;

          setAgent(agent as Agent);

          // Set capabilities from the populated data
          const actionsCount = ((agent as any).actions || []).length;
          const subAgentsCount = ((agent as any).sub_agents || []).length;

          setHasActions(actionsCount > 0);
          setHasSubAgents(subAgentsCount > 0);
          setCapabilitiesLoading(false);
          setLoadingState("loaded");

          // Load application details
          if ((agent as any).application_id) {
            applicationSubscription = ApplicationService.getApplication(
              (agent as any).application_id,
              () => {},
              apiKey
            ).subscribe(
              (appData: Application) => {
                setApplication(appData);
              },
              (error: any) => {
                console.error("Error loading application:", error);
              }
            );
          }
          // Initialize Agent Form editor from agent_config.form
          try {
            if (isCancelled) return;

            const cfg: any = (agent as any)?.agent_config || {};
            const rawForm = cfg?.form;
            const nextFields: Array<{
              name: string;
              description: string;
              type: string;
              updateRules?: string;
              readScope: string;
              writeScope: string;
              readAgents?: number[];
              writeAgents?: number[];
            }> = [];

            if (rawForm && rawForm.fields && Array.isArray(rawForm.fields)) {
              // New AgentForm structure with fields array
              for (const field of rawForm.fields) {
                if (field && typeof field === "object" && field.name) {
                  nextFields.push({
                    name: field.name,
                    description: field.description || "",
                    type: field.type || "string",
                    updateRules: field.updateRules || "",
                    readScope: field.readScope || "private",
                    writeScope: field.writeScope || "private",
                    readAgents: Array.isArray(field.readAgents)
                      ? field.readAgents
                      : [],
                    writeAgents: Array.isArray(field.writeAgents)
                      ? field.writeAgents
                      : [],
                  });
                }
              }
            } else if (rawForm && Array.isArray(rawForm)) {
              // Legacy array format
              for (const k of rawForm) {
                if (typeof k === "string") {
                  nextFields.push({
                    name: k,
                    description: "",
                    type: "string",
                    updateRules: "",
                    readScope: "private",
                    writeScope: "private",
                    readAgents: [],
                    writeAgents: [],
                  });
                }
              }
            } else if (rawForm && typeof rawForm === "object") {
              // Legacy dict format
              for (const k of Object.keys(rawForm)) {
                const v = (rawForm as any)[k];
                const inferred =
                  typeof v === "number"
                    ? "number"
                    : typeof v === "boolean"
                    ? "boolean"
                    : "string";
                nextFields.push({
                  name: k,
                  description: "",
                  type: inferred,
                  updateRules: "",
                  readScope: "private",
                  writeScope: "private",
                  readAgents: [],
                  writeAgents: [],
                });
              }
            }

            setFormFields(nextFields);
            setFormDirty(false);
          } catch (e) {
            // noop
          }
          // Initialize Special Instructions from agent_config.strategy_prompt_addendum
          try {
            if (isCancelled) return;

            const cfg: any = (agent as any)?.agent_config || {};
            const strategyRaw: string = cfg?.strategy_prompt_addendum || "";
            const parsedNotes: string[] =
              parseStrategyAddendumToNotes(strategyRaw);
            setSpecialInstructions(parsedNotes);
            setNotesDirty(false);
          } catch (e) {
            // noop
          }
          // Initialize structured instructions from agent_config.meta.instructions with legacy fallback
          try {
            if (isCancelled) return;

            const cfg: any = (agent as any)?.agent_config || {};
            const meta: any = cfg?.meta || {};
            const instr: any = meta?.instructions || {};
            const legacyAddendum = (
              cfg?.strategy_prompt_addendum || ""
            ).toString();
            // Backwards-compat: use legacy addendum to seed Orchestration Instructions if bucket missing
            setOrchestrationInstructions(
              instr?.orchestration || legacyAddendum
            );
            setStrategyInstructions(instr?.strategy || "");
            setProcessingInstructions(instr?.processing || "");
            setInstructionsDirty(false);
          } catch (e) {
            // noop
          }
        },
        (error: any) => {
          console.error("Error loading agent:", error);
          setLoadingState("error");
          setCapabilitiesLoading(false);
        }
      );
    }

    // Cleanup subscriptions on effect cleanup or component unmount
    return () => {
      isCancelled = true; // Mark this effect as cancelled
      if (agentSubscription) {
        agentSubscription.unsubscribe();
      }
      if (applicationSubscription) {
        applicationSubscription.unsubscribe();
      }
    };
  }, [agentId, apiKey, isVersionView, version]);

  // Helper: Reload version status (for draft view only)
  const reloadVersionStatus = useCallback(() => {
    if (!agentId || isVersionView) return;
    AgentService.getAgentVersionStatus(Number(agentId), () => {}, apiKey).subscribe(
      (res: any) => {
        setLatestVersion(res.latest_version);
        setHasUnpublishedChanges(!!res.has_unpublished_changes);
        // Dispatch event for child components
        window.dispatchEvent(new CustomEvent("agentVersionStatusUpdated", {
          detail: { agentId: Number(agentId), versionStatus: res }
        }));
      },
      (err: any) => console.error("Error loading agent version status:", err)
    );
  }, [agentId, apiKey, isVersionView]);

  // Helper: Draft from version
  const handleDraftFromVersion = useCallback(async (versionToDraft: string) => {
    if (!agentId || !versionToDraft || isDraftingFromVersion) return;
    
    // Open confirmation dialog
    setPendingDraftVersion(versionToDraft);
    setDraftConfirmDialogOpen(true);
  }, [agentId, isDraftingFromVersion]);

  // Confirm and execute draft from version
  const confirmDraftFromVersion = useCallback(() => {
    if (!agentId || !pendingDraftVersion || isDraftingFromVersion) return;
    
    setDraftConfirmDialogOpen(false);
    setIsDraftingFromVersion(true); // Immediate feedback - show loading state
    
    // Create draft FIRST, then navigate after it's ready
    AgentService.draftFromVersion(Number(agentId), pendingDraftVersion, () => {}, apiKey).subscribe(
      () => {
        // Draft created successfully - backend already waited for hash computation
        // Now navigate to draft view (if we were viewing a version)
    if (isVersionView && version) {
      const params = new URLSearchParams(searchParams);
      params.delete("version");
      navigate(`/agent/${agentId}?${params.toString()}`);
    }
    
        // Reload version status and agent data immediately
        // Backend already returned refreshed status, so we can reload right away
          reloadVersionStatus();
          reloadAgent();
          setIsDraftingFromVersion(false);
          setPendingDraftVersion(null);
      },
      (err: any) => {
        console.error("Error drafting from version:", err);
        toast.error("Failed to create draft from version");
        setIsDraftingFromVersion(false);
        setPendingDraftVersion(null);
      }
    );
  }, [agentId, apiKey, isVersionView, version, searchParams, navigate, reloadVersionStatus, reloadAgent, isDraftingFromVersion, pendingDraftVersion]);

  // Helper: Ensure draft exists before any edit action (if viewing draft and no unpublished changes)
  const ensureDraftBeforeAction = useCallback(async (action: () => void) => {
    // If viewing a version, don't create draft automatically - just allow the action
    // The draft will be created when they try to save
    if (isVersionView) {
      action();
      return;
    }
    
    // If there's already a draft (has unpublished changes), proceed with action
    if (hasUnpublishedChanges) {
      action();
      return;
    }
    
    // If there's a latest version but no draft, create draft first
    if (latestVersion) {
      setIsDraftingFromVersion(true); // Immediate feedback - show loading state
      AgentService.draftFromVersion(Number(agentId), latestVersion, () => {}, apiKey).subscribe(
        () => {
          // Draft created successfully - backend already waited for hash computation
          // Reload to get updated state, then execute action immediately
            reloadVersionStatus();
            reloadAgent();
            setIsDraftingFromVersion(false);
            // Execute the action after draft is ready
            action();
        },
        (err: any) => {
          console.error("Error creating draft:", err);
          setIsDraftingFromVersion(false);
        }
      );
    } else {
      // No version exists yet, proceed with action
      action();
    }
  }, [agentId, apiKey, isVersionView, latestVersion, hasUnpublishedChanges, reloadVersionStatus, reloadAgent]);

  // Helper: Check if draft needs to be created before saving (when viewing a version)
  const checkDraftBeforeSave = useCallback((saveAction: () => void) => {
    // If viewing a version, show confirmation dialog
    if (isVersionView && version) {
      setPendingSaveAction(() => saveAction);
      setSaveConfirmDialogOpen(true);
      return;
    }
    
    // If there's already a draft (has unpublished changes), proceed with save
    if (hasUnpublishedChanges) {
      saveAction();
      return;
    }
    
    // If there's a latest version but no draft, create draft first
    if (latestVersion) {
      setIsDraftingFromVersion(true); // Immediate feedback - show loading state
      AgentService.draftFromVersion(Number(agentId), latestVersion, () => {}, apiKey).subscribe(
        () => {
          // Draft created successfully - backend already waited for hash computation
          // Reload to get updated state, then execute save immediately
            reloadVersionStatus();
            reloadAgent();
            setIsDraftingFromVersion(false);
            saveAction();
        },
        (err: any) => {
          console.error("Error creating draft:", err);
          setIsDraftingFromVersion(false);
        }
      );
    } else {
      // No version exists yet, proceed with save
      saveAction();
    }
  }, [agentId, apiKey, isVersionView, version, latestVersion, hasUnpublishedChanges, reloadVersionStatus, reloadAgent]);

  // Confirm save and create draft from version
  const confirmSaveWithDraft = useCallback(() => {
    if (!agentId || !version || !pendingSaveAction) return;
    
    setSaveConfirmDialogOpen(false);
    setIsDraftingFromVersion(true); // Immediate feedback - show loading state
    
    // Create draft FIRST, then navigate after it's ready
    AgentService.draftFromVersion(Number(agentId), version, () => {}, apiKey).subscribe(
      () => {
        // Draft created successfully - backend already waited for hash computation
        // Now navigate to draft view
    const params = new URLSearchParams(searchParams);
    params.delete("version");
    navigate(`/agent/${agentId}?${params.toString()}`);
    
        // Reload version status and agent data immediately, then execute save
        // Backend already returned refreshed status, so we can reload right away
          reloadVersionStatus();
          reloadAgent();
          setIsDraftingFromVersion(false);
          if (pendingSaveAction) {
            pendingSaveAction();
          }
          setPendingSaveAction(null);
      },
      (err: any) => {
        console.error("Error creating draft:", err);
        setIsDraftingFromVersion(false);
        setPendingSaveAction(null);
      }
    );
  }, [agentId, apiKey, version, pendingSaveAction, searchParams, navigate, reloadVersionStatus, reloadAgent]);
  useEffect(() => {
    if (!agentId || isDraftingFromVersion) return;
    // Reload version status when agentId or version changes (but not when viewing a version, as status doesn't change)
    reloadVersionStatus();
  }, [agentId, apiKey, version, isDraftingFromVersion, reloadVersionStatus]);

  // Listen for version status updates from child components (e.g., AgentVersionsTable)
  useEffect(() => {
    const handleVersionStatusUpdate = (event: any) => {
      const { agentId: eventAgentId, versionStatus } = event.detail || {};
      if (eventAgentId && Number(eventAgentId) === Number(agentId)) {
        setLatestVersion(versionStatus?.latest_version);
        setHasUnpublishedChanges(!!versionStatus?.has_unpublished_changes);
      }
    };

    window.addEventListener("agentVersionStatusUpdated", handleVersionStatusUpdate);
    return () => {
      window.removeEventListener("agentVersionStatusUpdated", handleVersionStatusUpdate);
    };
  }, [agentId]);

  // Listen for agent reload requests from child components (e.g., AgentVersionsTable three dots menu)
  useEffect(() => {
    const handleAgentReloadRequest = (event: any) => {
      const { agentId: eventAgentId } = event.detail || {};
      if (eventAgentId && Number(eventAgentId) === Number(agentId)) {
        // Reload agent data (same as top button does)
        reloadAgent();
      }
    };

    window.addEventListener("agentReloadRequested", handleAgentReloadRequest);
    return () => {
      window.removeEventListener("agentReloadRequested", handleAgentReloadRequest);
    };
  }, [agentId, reloadAgent]);


  // Allow deep-linking to a specific tab via query param
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (
      tab &&
      [
        "overview",
        "configuration",
        "capabilities",
        "sessions",
        "versions",
      ].includes(tab)
    ) {
      setActiveTab(tab);
    }
    const configTab = searchParams.get("configTab");
    if (configTab && ["routing", "form", "instructions"].includes(configTab)) {
      setConfigSubTab(configTab);
    }
  }, [searchParams]);

  // Update instruction variables when agent data changes
  useEffect(() => {
    if (agent) {
      try {
        const cfg: any = (agent as any)?.agent_config || {};
        const meta: any = cfg?.meta || {};
        const instr: any = meta?.instructions || {};
        const legacyAddendum = (cfg?.strategy_prompt_addendum || "").toString();

        // Update instruction variables from agent data
        setOrchestrationInstructions(instr?.orchestration || legacyAddendum);
        setStrategyInstructions(instr?.strategy || "");
        setProcessingInstructions(instr?.processing || "");
        setInstructionsDirty(false);
      } catch (e) {
        console.error(
          "Error updating instruction variables from agent data:",
          e
        );
      }
    }
  }, [agent]);

  // Separate useEffect for loading breadcrumb agents when URL params change
  useEffect(() => {
    let subscription: any = null;

    if (parentsChain && apiKey) {
      const agentIds = parentsChain
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));

      if (agentIds.length > 0) {
        setBreadcrumbsLoading(true);
        subscription = AgentService.getAgentsBulk(
          agentIds,
          () => {},
          apiKey
        ).subscribe(
          (response: any) => {
            const agents = response.agents || [];
            // Sort agents to match the order in the parent chain
            const sortedAgents = agentIds
              .map((id) => agents.find((agent: Agent) => agent.id === id))
              .filter(Boolean);
            setBreadcrumbAgents(sortedAgents);
            setBreadcrumbsLoading(false);
          },
          (error: any) => {
            console.error("Error loading breadcrumb agents:", error);
            setBreadcrumbAgents([]);
            setBreadcrumbsLoading(false);
          }
        );
      } else {
        setBreadcrumbAgents([]);
        setBreadcrumbsLoading(false);
      }
    } else {
      // Clear breadcrumb agents if no parent chain
      setBreadcrumbAgents([]);
      setBreadcrumbsLoading(false);
    }

    // Cleanup subscription on effect cleanup or component unmount
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [parentsChain, apiKey, agentId]);

  const handleCreateConversationFlow = () => {
    // Navigate to conversation tree builder with agent context
    navigate(
      `/admin/conversation-tree?agent_id=${agentId}&application_ref_name=${application?.application_ref_name}`
    );
  };

  const handleAddFirstCapability = () => {
    // Navigate to capabilities tab
    setActiveTab("capabilities");
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset values
      setEditedName(agent?.agent_name || "");
      setEditedDescription(agent?.description || "");
      setEditedBaseUrl((agent?.agent_config as any)?.base_url || "");
      setEditedStrategyAddendum(
        (agent?.agent_config as any)?.strategy_prompt_addendum || ""
      );
      setBaseUrlError(null);
      setIsEditing(false);
    } else {
      // Ensure draft exists before editing (if needed)
      ensureDraftBeforeAction(() => {
        // Start editing - initialize values
        setEditedName(agent?.agent_name || "");
        setEditedDescription(agent?.description || "");
        setEditedBaseUrl((agent?.agent_config as any)?.base_url || "");
        setEditedStrategyAddendum(
          (agent?.agent_config as any)?.strategy_prompt_addendum || ""
        );
        setBaseUrlError(null);
        setIsEditing(true);
      });
    }
  };

  const handleSave = () => {
    if (!agentId || !agent) return;

    // Check if we need to create a draft before saving
    const performSave = () => {
      // Validate base_url if provided
    if (editedBaseUrl && editedBaseUrl.trim()) {
      try {
        new URL(editedBaseUrl);
        setBaseUrlError(null);
      } catch (error) {
        setBaseUrlError(
          "Please enter a valid URL (e.g., https://api.example.com)"
        );
        return;
      }
    } else {
      setBaseUrlError(null);
    }

    setIsSaving(true);
    // Merge into agent_config
    const mergedAgentConfig: any = { ...((agent?.agent_config as any) || {}) };
    const trimmedBaseUrl = (editedBaseUrl || "").trim();
    const trimmedNotes = (editedStrategyAddendum || "").trim();

    if (trimmedBaseUrl) {
      mergedAgentConfig.base_url = trimmedBaseUrl;
    } else if ("base_url" in mergedAgentConfig) {
      delete mergedAgentConfig.base_url;
    }

    if (trimmedNotes) {
      mergedAgentConfig.strategy_prompt_addendum = trimmedNotes;
    } else if ("strategy_prompt_addendum" in mergedAgentConfig) {
      delete mergedAgentConfig.strategy_prompt_addendum;
    }

    const updates = {
      agent_name: editedName.trim(),
      description: editedDescription.trim(),
      agent_config: mergedAgentConfig,
    };

    AgentService.updateAgent(
      Number(agentId),
      updates,
      () => {},
      apiKey
    ).subscribe(
      (updatedAgent: Agent) => {
        // Always use the updated agent from the backend response
        setAgent(updatedAgent);
        setIsEditing(false);
        setIsSaving(false);
        // Reload version status to update badge
        reloadVersionStatus();
      },
      (error: any) => {
        console.error("Error updating agent:", error);
        setIsSaving(false);
      }
    );
    };
    
    // Check if we need to create a draft before saving
    checkDraftBeforeSave(performSave);
  };

  const handleBaseUrlChange = (value: string) => {
    setEditedBaseUrl(value);

    // Validate base_url on change
    if (value && value.trim()) {
      try {
        new URL(value);
        setBaseUrlError(null);
      } catch (error) {
        setBaseUrlError(
          "Please enter a valid URL (e.g., https://api.example.com)"
        );
      }
    } else {
      setBaseUrlError(null);
    }
  };

  // Agent Form helpers
  const addEmptyFormField = () => {
    setFormFields((prev) => [
      ...prev,
      {
        name: "",
        description: "",
        type: "string",
        updateRules: "",
        readScope: "private",
        writeScope: "private",
        readAgents: [],
        writeAgents: [],
      },
    ]);
    setFormDirty(true);
  };

  const updateFormField = (
    index: number,
    key:
      | "name"
      | "description"
      | "type"
      | "updateRules"
      | "readScope"
      | "writeScope",
    value: string
  ) => {
    setFormFields((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [key]: value } as any;
      return copy;
    });
    setFormDirty(true);
  };

  const bulkMoveFormFields = (fieldNames: string[], targetAgentId: number) => {
    if (fieldNames.length === 0) return;

    const targetAgent = (agent?.sub_agents || []).find(
      (sa: any) => sa.id === targetAgentId
    );
    if (!targetAgent) return;

    // Show confirmation dialog
    setPendingFieldAction({
      type: "bulkMove",
      fieldNames,
      targetAgentId,
      targetAgentName: targetAgent.agent_name,
    });
    setFieldMoveDialogOpen(true);
  };

  const executeBulkMove = () => {
    if (!pendingFieldAction || pendingFieldAction.type !== "bulkMove") return;
    const { fieldNames, targetAgentId, targetAgentName } = pendingFieldAction;
    if (!fieldNames || !targetAgentId) return;

    const performBulkMove = () => {
      const fieldCount = fieldNames.length;

      AgentService.moveFormFields(
        Number(agentId),
        targetAgentId,
        fieldNames,
        () => {},
        apiKey
      ).subscribe({
        next: () => {
          // Remove moved fields from current state
          setFormFields((prev) =>
            prev.filter((f) => !fieldNames.includes(f.name))
          );
          setFormDirty(false);
          toast.success(
            `${fieldCount} field${fieldCount > 1 ? "s" : ""} moved to ${targetAgentName}`
          );
        },
        error: (err: any) => {
          console.error("Failed to move fields:", err);
          toast.error("Failed to move fields");
        },
      });
    };

    setFieldMoveDialogOpen(false);
    setPendingFieldAction(null);
    checkDraftBeforeSave(performBulkMove);
  };

  const bulkDeleteFormFields = (fieldNames: string[]) => {
    if (fieldNames.length === 0) return;

    // Show confirmation dialog
    setPendingFieldAction({
      type: "delete",
      fieldNames,
    });
    setFieldDeleteDialogOpen(true);
  };

  const executeBulkDelete = () => {
    if (!pendingFieldAction || pendingFieldAction.type !== "delete") return;
    const { fieldNames } = pendingFieldAction;
    if (!fieldNames) return;

    const performBulkDelete = () => {
      const fieldCount = fieldNames.length;

      // Remove fields from state
      const updatedFields = formFields.filter(
        (f) => !fieldNames.includes(f.name)
      );
      setFormFields(updatedFields);

      // Auto-save
      const validFields = updatedFields.filter((field) => field.name.trim());
      const agentFormData = {
        fields: validFields.map((field) => ({
          name: field.name.trim(),
          type: field.type,
          description: field.description || "",
          updateRules: field.updateRules || "",
          readScope: field.readScope || "private",
          writeScope: field.writeScope || "private",
          readAgents: field.readAgents || [],
          writeAgents: field.writeAgents || [],
        })),
      };

      const mergedAgentConfig: any = {
        ...(((agent as any)?.agent_config as any) || {}),
      };
      mergedAgentConfig.form = agentFormData;
      const updates: any = { agent_config: mergedAgentConfig };

      AgentService.updateAgent(
        Number(agentId),
        updates,
        () => {},
        apiKey
      ).subscribe(
        () => {
          setAgent((prevAgent: any) => ({
            ...prevAgent,
            agent_config: mergedAgentConfig,
          }));
          toast.success(`${fieldCount} field${fieldCount > 1 ? "s" : ""} deleted`);
        },
        (error: any) => {
          console.error("Error deleting fields:", error);
          toast.error("Failed to delete fields");
        }
      );
    };

    setFieldDeleteDialogOpen(false);
    setPendingFieldAction(null);
    checkDraftBeforeSave(performBulkDelete);
  };

  const moveFormField = (fieldIndex: number, targetAgentId: number) => {
    const fieldToMove = formFields[fieldIndex];
    if (!fieldToMove || !fieldToMove.name.trim()) return;

    // Find the target agent name
    const targetAgent = (agent?.sub_agents || []).find(
      (sa: any) => sa.id === targetAgentId
    );
    if (!targetAgent) return;

    // Show confirmation dialog
    setPendingFieldAction({
      type: "move",
      fieldIndex,
      fieldNames: [fieldToMove.name],
      targetAgentId,
      targetAgentName: targetAgent.agent_name,
    });
    setFieldMoveDialogOpen(true);
  };

  const executeSingleMove = () => {
    if (!pendingFieldAction || pendingFieldAction.type !== "move") return;
    const { fieldIndex, targetAgentId, targetAgentName } = pendingFieldAction;
    if (fieldIndex === undefined || !targetAgentId) return;

    const performMove = () => {
      const fieldToMove = formFields[fieldIndex];
      if (!fieldToMove) return;

      // Remove from current agent
      const updatedFields = formFields.filter((_, i) => i !== fieldIndex);
      setFormFields(updatedFields);

      // Save current agent without this field
      const validFields = updatedFields.filter((field) => field.name.trim());
      const currentAgentFormData = {
        fields: validFields.map((field) => ({
          name: field.name.trim(),
          type: field.type,
          description: field.description?.trim() || "",
          updateRules: field.updateRules?.trim() || "",
          readScope: field.readScope || "private",
          writeScope: field.writeScope || "private",
          readAgents: field.readAgents || [],
          writeAgents: field.writeAgents || [],
        })),
      };

      // Get target agent's current form fields and add this field
      AgentService.getAgent(targetAgentId, () => {}, apiKey).subscribe({
        next: (targetAgentData: Agent) => {
          // Handle both array format and object format with 'fields' property
          const targetForm = (targetAgentData.agent_config as any)?.form;
          let targetFormFields = [];
          if (Array.isArray(targetForm)) {
            targetFormFields = targetForm;
          } else if (targetForm && Array.isArray(targetForm.fields)) {
            targetFormFields = targetForm.fields;
          }

          const newTargetFields = [
            ...targetFormFields,
            {
              name: fieldToMove.name.trim(),
              type: fieldToMove.type,
              description: fieldToMove.description?.trim() || "",
              updateRules: fieldToMove.updateRules?.trim() || "",
              readScope: fieldToMove.readScope || "public",
              writeScope: fieldToMove.writeScope || "public",
              readAgents: fieldToMove.readAgents || [],
              writeAgents: fieldToMove.writeAgents || [],
            },
          ];

          // Update current agent
          const currentMergedConfig: any = {
            ...(((agent as any)?.agent_config as any) || {}),
          };
          currentMergedConfig.form = currentAgentFormData;
          const currentUpdates: any = { agent_config: currentMergedConfig };

          AgentService.updateAgent(
            Number(agentId),
            currentUpdates,
            () => {},
            apiKey
          ).subscribe({
            next: () => {
              // Update target agent
              const targetMergedConfig: any = {
                ...((targetAgentData.agent_config as any) || {}),
              };
              targetMergedConfig.form = { fields: newTargetFields };
              const targetUpdates: any = { agent_config: targetMergedConfig };

              AgentService.updateAgent(
                targetAgentId,
                targetUpdates,
                () => {},
                apiKey
              ).subscribe({
                next: () => {
                  setFormDirty(false);
                  setAgent((prevAgent: any) => ({
                    ...prevAgent,
                    agent_config: currentMergedConfig,
                  }));
                  toast.success(
                    `Field "${fieldToMove.name}" moved to ${targetAgentName}`
                  );
                },
                error: (err: any) => {
                  console.error("Failed to add field to target agent:", err);
                  toast.error("Failed to move field to target agent");
                  // Revert the change
                  setFormFields(formFields);
                },
              });
            },
            error: (err: any) => {
              console.error("Failed to remove field from current agent:", err);
              toast.error("Failed to move field");
              // Revert the change
              setFormFields(formFields);
            },
          });
        },
        error: (err: any) => {
          console.error("Failed to fetch target agent:", err);
          toast.error("Failed to fetch target agent");
          setFormFields(formFields);
        },
      });
    };

    setFieldMoveDialogOpen(false);
    setPendingFieldAction(null);
    checkDraftBeforeSave(performMove);
  };

  const removeFormField = (index: number) => {
    const performRemove = () => {
      const updatedFields = formFields.filter((_, i) => i !== index);
      setFormFields(updatedFields);

      // Clamp the current page if it exceeds total pages after deletion
      const totalPages = Math.ceil(updatedFields.length / itemsPerPage);
      if (formFieldsPage > totalPages && totalPages > 0) {
        setFormFieldsPage(totalPages);
      }

      // Auto-save
      const validFields = updatedFields.filter((field) => field.name.trim());
      const agentFormData = {
        fields: validFields.map((field) => ({
          name: field.name.trim(),
          type: field.type,
          description: field.description || "",
          updateRules: field.updateRules || "",
          readScope: field.readScope || "private",
          writeScope: field.writeScope || "private",
        })),
      };

      const mergedAgentConfig: any = {
        ...(((agent as any)?.agent_config as any) || {}),
      };
      mergedAgentConfig.form = agentFormData;
      const updates: any = { agent_config: mergedAgentConfig };

      AgentService.updateAgent(
        Number(agentId),
        updates,
        () => {},
        apiKey
      ).subscribe(
        () => {
          setAgent((prevAgent: any) => ({
            ...prevAgent,
            agent_config: mergedAgentConfig,
          }));
        },
        (error: any) => {
          console.error("Error saving agent form:", error);
        }
      );
    };
    
    checkDraftBeforeSave(performRemove);
  };

  // Form field modal handlers
  const openFormFieldModal = (index: number | null = null) => {
    ensureDraftBeforeAction(() => {
      setEditingFormFieldIndex(index);
      setFormFieldModalOpen(true);
    });
  };

  const saveFormFieldFromModal = (field: {
    name: string;
    description: string;
    type: string;
    updateRules?: string;
    readScope: string;
    writeScope: string;
    readAgents?: number[];
    writeAgents?: number[];
  }) => {
    const performSave = () => {
      let updatedFields;
      if (editingFormFieldIndex !== null) {
        // Edit existing
        updatedFields = formFields.map((f, i) =>
          i === editingFormFieldIndex ? field : f
        );
      } else {
        // Add new
        updatedFields = [...formFields, field];
      }
      setFormFields(updatedFields);
      setFormFieldModalOpen(false);
      setEditingFormFieldIndex(null);

      // Auto-save
      const validFields = updatedFields.filter((f) => f.name.trim());
      const agentFormData = {
        fields: validFields.map((f) => ({
          name: f.name.trim(),
          type: f.type,
          description: f.description || "",
          updateRules: f.updateRules || "",
          readScope: f.readScope || "public",
          writeScope: f.writeScope || "public",
          readAgents: Array.isArray(f.readAgents) ? f.readAgents : [],
          writeAgents: Array.isArray(f.writeAgents) ? f.writeAgents : [],
        })),
      };

      const mergedAgentConfig: any = {
        ...(((agent as any)?.agent_config as any) || {}),
      };
      mergedAgentConfig.form = agentFormData;
      const updates: any = { agent_config: mergedAgentConfig };

      AgentService.updateAgent(
        Number(agentId),
        updates,
        () => {},
        apiKey
      ).subscribe(
        () => {
          setFormFields(validFields);
          setAgent((prevAgent: any) => ({
            ...prevAgent,
            agent_config: mergedAgentConfig,
          }));
        },
        (error: any) => {
          console.error("Error saving agent form:", error);
        }
      );
    };
    
    checkDraftBeforeSave(performSave);
  };

  // Special instruction modal handlers
  const openInstructionModal = (index: number | null = null) => {
    if (index !== null) {
      setEditingInstructionIndex(index);
      setTempInstruction(specialInstructions[index]);
    } else {
      setEditingInstructionIndex(null);
      setTempInstruction("");
    }
    setInstructionModalOpen(true);
  };

  const saveInstructionFromModal = () => {
    let updatedInstructions;
    if (editingInstructionIndex !== null) {
      // Edit existing
      updatedInstructions = specialInstructions.map((inst, i) =>
        i === editingInstructionIndex ? tempInstruction : inst
      );
    } else {
      // Add new
      updatedInstructions = [...specialInstructions, tempInstruction];
    }
    setSpecialInstructions(updatedInstructions);
    setInstructionModalOpen(false);

    // Auto-save
    const joined = computeStrategyAddendumFromNotes(updatedInstructions);
    const mergedAgentConfig: any = {
      ...(((agent as any)?.agent_config as any) || {}),
    };
    mergedAgentConfig.strategy_prompt_addendum = joined;

    AgentService.updateAgent(
      Number(agentId),
      { agent_config: mergedAgentConfig },
      () => {},
      apiKey
    ).subscribe(
      () => {
        setAgent((prevAgent: any) => ({
          ...prevAgent,
          agent_config: mergedAgentConfig,
        }));
      },
      (error: any) => {
        console.error("Error saving special instructions:", error);
      }
    );
  };

  // Helper functions for saving structured instructions from prompt modals
  const saveStructuredInstructions = (
    orchestration: string,
    strategy: string,
    processing: string
  ) => {
    if (!agentId || !agent || instructionsSaving) return;

    setInstructionsSaving(true);
    const mergedAgentConfig: any = {
      ...(((agent as any)?.agent_config as any) || {}),
    };
    const meta: any = { ...(mergedAgentConfig.meta || {}) };
    meta.instructions = {
      orchestration: orchestration.trim(),
      strategy: strategy.trim(),
      processing: processing.trim(),
    };
    mergedAgentConfig.meta = meta;

    // Legacy compatibility: map Orchestration Instructions to strategy_prompt_addendum
    const orchestrationTrimmed = orchestration.trim();
    if (orchestrationTrimmed) {
      mergedAgentConfig.strategy_prompt_addendum = orchestrationTrimmed;
    } else if ("strategy_prompt_addendum" in mergedAgentConfig) {
      delete mergedAgentConfig.strategy_prompt_addendum;
    }

    AgentService.updateAgent(
      Number(agentId),
      { agent_config: mergedAgentConfig },
      () => {},
      apiKey
    ).subscribe(
      () => {
        setInstructionsDirty(false);
        setInstructionsSaving(false);
        setAgent((prevAgent: any) => ({
          ...prevAgent,
          agent_config: mergedAgentConfig,
        }));
      },
      (error: any) => {
        console.error("Error saving instructions:", error);
        setInstructionsSaving(false);
      }
    );
  };

  const handleOrchestrationSave = (value: string) => {
    setOrchestrationInstructions(value);
    setInstructionsDirty(true);
    setOrchestrationModalOpen(false);
    checkDraftBeforeSave(() => {
    saveStructuredInstructions(
      value,
      strategyInstructions,
      processingInstructions
    );
    });
  };

  const handleStrategySave = (value: string) => {
    setStrategyInstructions(value);
    setInstructionsDirty(true);
    setStrategyModalOpen(false);
    saveStructuredInstructions(
      orchestrationInstructions,
      value,
      processingInstructions
    );
  };

  const handleProcessingSave = (value: string) => {
    setProcessingInstructions(value);
    setInstructionsDirty(true);
    setProcessingModalOpen(false);
    saveStructuredInstructions(
      orchestrationInstructions,
      strategyInstructions,
      value
    );
  };

  const saveAgentForm = () => {
    if (!agentId || !agent || formSaving) return;

    const performSave = () => {
      setFormSaving(true);

      // Create AgentForm structure with fields array
      const agentFormData = {
        fields: formFields
          .map((field) => ({
            name: field.name.trim(),
            type: field.type,
            description: field.description || "",
            updateRules: field.updateRules || "",
            readScope: field.readScope || "private",
            writeScope: field.writeScope || "private",
            readAgents: Array.isArray(field.readAgents) ? field.readAgents : [],
            writeAgents: Array.isArray(field.writeAgents)
              ? field.writeAgents
              : [],
          }))
          .filter((field) => field.name), // Only include fields with names
      };

      const mergedAgentConfig: any = {
        ...(((agent as any)?.agent_config as any) || {}),
      };
      mergedAgentConfig.form = agentFormData;
      const updates: any = { agent_config: mergedAgentConfig };
      AgentService.updateAgent(
        Number(agentId),
        updates,
        () => {},
        apiKey
      ).subscribe(
        () => {
          // Filter out empty fields for the final saved state
          const savedFields = formFields.filter((field) => field.name.trim());
          setFormFields(savedFields);
          setFormDirty(false);
          setFormSaving(false);

          // Update the agent object locally with the new config
          setAgent((prevAgent: any) => ({
            ...prevAgent,
            agent_config: mergedAgentConfig,
          }));
        },
        (error: any) => {
          console.error("Error saving agent form:", error);
          setFormSaving(false);
        }
      );
    };
    
    checkDraftBeforeSave(performSave);
  };

  // Analytics fetch function
  const fetchRequestAnalytics = useCallback(
    (period: QuickPeriod) => {
      if (!agentId) return;

      setRequestAnalyticsLoading(true);

      const now = moment();
      const days = period.days;

      let startDate: string;
      let endDate: string;

      if (days === 1) {
        // For 1D period, get true 24 hours: from 24 hours ago to now (in UTC)
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
        startDate = now
          .clone()
          .subtract(24, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      } else {
        // For multi-day periods, use the same logic as 1D but with more hours
        const totalHours = days * 24;
        endDate = now.clone().utc().format("YYYY-MM-DD HH:mm:ss");
        startDate = now
          .clone()
          .subtract(totalHours, "hours")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      }

      setRequestAnalyticsPeriodDays(days);

      AnalyticsService.getAgentRequestAnalytics(
        Number(agentId),
        setRequestAnalyticsLoading,
        apiKey,
        startDate,
        endDate
      ).subscribe(
        (response: any) => {
          setRequestAnalyticsData(response.data || []);
        },
        (error: any) => {
          console.error("Error fetching agent request analytics:", error);
          setRequestAnalyticsData([]);
        }
      );
    },
    [agentId, apiKey]
  );

  const handleRequestAnalyticsPeriodSelect = (period: QuickPeriod) => {
    setRequestAnalyticsPeriod(period);
    fetchRequestAnalytics(period);
  };

  // Load request analytics when overview tab becomes active
  useEffect(() => {
    if (activeTab === "overview") {
      fetchRequestAnalytics(requestAnalyticsPeriod);
    }
  }, [activeTab, fetchRequestAnalytics, requestAnalyticsPeriod]);

  // Check if agent has any capabilities
  const hasCapabilities = hasActions || hasSubAgents;

  // Tab configuration
  const tabs: TabItem[] = [
    {
      key: "configuration",
      label: "Configuration",
      icon: <SettingsIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6 relative">
          {isDraftingFromVersion && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-maitai-green" />
                <p className="text-sm text-muted-foreground font-gc-med">
                  Creating draft and loading changes...
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">
                Agent Configuration
              </h3>
              <p className="text-sm text-muted-foreground">
                Configure routing, form fields, and instructions for your agent
              </p>
            </div>
            <div className="flex items-center gap-2">
              {configSubTab === "form" && (
                <Button
                  onClick={() => openFormFieldModal()}
                  className="gap-2"
                  size="sm"
                  disabled={isDraftingFromVersion}>
                  <PlusIcon className="w-4 h-4" />
                  Add New Field
                </Button>
              )}
            </div>
          </div>

          {/* Sub-tabs */}
          <div className="border-b border-border">
            <nav className="flex space-x-8">
              <button
                onClick={() => setConfigSubTab("routing")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  configSubTab === "routing"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Routing
              </button>
              <button
                onClick={() => setConfigSubTab("form")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  configSubTab === "form"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Agent Form
              </button>
              <button
                onClick={() => setConfigSubTab("instructions")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  configSubTab === "instructions"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Instructions
              </button>
            </nav>
          </div>

          {/* Sub-tab content */}
          {configSubTab === "routing" && (
            <AgentRoutingConfig
              agent={agent}
              agentId={agentId || ""}
              apiKey={apiKey}
              onAgentUpdate={(updatedAgent) => setAgent(updatedAgent)}
              orchestrationInstructions={orchestrationInstructions}
              isVersionView={isVersionView}
              onOrchestrationEdit={() => setOrchestrationModalOpen(true)}
              formFields={formFields}
              ensureDraftBeforeAction={ensureDraftBeforeAction}
              checkDraftBeforeSave={checkDraftBeforeSave}
            />
          )}

          {configSubTab === "form" && (
            <div className="space-y-4">
              <AgentFormFieldsTable
                formFields={formFields}
                visibleFormFields={visibleFormFields}
                formSearchQuery={formSearchQuery}
                onSearchChange={setFormSearchQuery}
                formSortBy={formSortBy}
                formSortDir={formSortDir}
                onToggleSort={toggleFormSort}
                formFieldsPage={formFieldsPage}
                onPageChange={setFormFieldsPage}
                itemsPerPage={itemsPerPage}
                onFieldClick={openFormFieldModal}
                onFieldRemove={removeFormField}
                onFieldMove={moveFormField}
                onBulkMove={bulkMoveFormFields}
                onBulkDelete={bulkDeleteFormFields}
                isVersionView={isVersionView}
                subagents={(agent?.sub_agents || []).map((sa: any) => ({
                  id: sa.id,
                  agent_name: sa.agent_name,
                }))}
              />
            </div>
          )}

          {configSubTab === "instructions" && (
            <div className="space-y-6">
              <div className="text-sm text-muted-foreground">
                Configure guidance for strategy building and processing. (Note:
                Orchestration instructions are in the Routing tab)
              </div>

              <Card className="border-border">
                <CardContent className="p-9 space-y-9">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label>Strategy Instructions</Label>
                      <Tooltip content="Strategy guidance: how to best build strategies for chaining capabilities and accomplishing the request.">
                        <Info className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Button
                      onClick={() => setStrategyModalOpen(true)}
                      variant="outline"
                      className="w-full h-24 justify-start items-start text-left p-4 font-normal">
                      <div className="w-full">
                        {strategyInstructions ? (
                          <div className="space-y-2">
                            <div className="text-sm text-muted-foreground">
                              {strategyInstructions.length} characters •{" "}
                              {strategyInstructions.split("\n").length} lines
                            </div>
                            <div className="text-sm line-clamp-2 text-foreground">
                              {strategyInstructions}
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted-foreground">
                            Click to add strategy instructions...
                          </div>
                        )}
                      </div>
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Label>Processing Instructions</Label>
                      <Tooltip content="Rules for updating agent context, summaries, and state after actions.">
                        <Info className="w-4 h-4 text-muted-foreground" />
                      </Tooltip>
                    </div>
                    <Button
                      onClick={() => setProcessingModalOpen(true)}
                      variant="outline"
                      className="w-full h-24 justify-start items-start text-left p-4 font-normal">
                      <div className="w-full">
                        {processingInstructions ? (
                          <div className="space-y-2">
                            <div className="text-sm text-muted-foreground">
                              {processingInstructions.length} characters •{" "}
                              {processingInstructions.split("\n").length} lines
                            </div>
                            <div className="text-sm line-clamp-2 text-foreground">
                              {processingInstructions}
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted-foreground">
                            Click to add processing instructions...
                          </div>
                        )}
                      </div>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "overview",
      label: "Overview",
      icon: <UserIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-8">
          {/* Agent Details */}
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-gc-med text-foreground">
                  Agent Details
                </h3>
                <div className="flex items-center gap-2">
                  {isEditing && (
                    <Button
                      onClick={handleSave}
                      disabled={
                        isSaving || !editedName.trim() || !!baseUrlError
                      }
                      size="sm"
                      className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white">
                      <CheckIcon className="w-4 h-4" />
                      {isSaving ? "Saving..." : "Save"}
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      ensureDraftBeforeAction(() => {
                        setEditModalOpen(true);
                      });
                    }}
                    variant="ghost"
                    size="sm"
                    disabled={isDraftingFromVersion}
                    className="gap-2 text-muted-foreground hover:text-foreground">
                    <>
                      <EditIcon className="w-4 h-4" />
                      {isDraftingFromVersion ? "Creating Draft..." : "Edit"}
                    </>
                  </Button>
                </div>
                <AgentEditModal
                  open={editModalOpen}
                  onClose={() => setEditModalOpen(false)}
                  onAgentUpdated={() => {
                    reloadAgent();
                    setEditModalOpen(false);
                  }}
                  agent={agent as Agent}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                      Agent Name
                    </p>
                    {isEditing ? (
                      <Input
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        placeholder="Enter agent name"
                        className="text-base font-gc-med"
                        disabled={isSaving}
                      />
                    ) : (
                      <p className="text-base font-gc-med text-foreground">
                        {agent?.agent_name}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                      Description
                    </p>
                    {isEditing ? (
                      <Textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        placeholder="Enter agent description"
                        className="text-base font-gc-reg min-h-[80px]"
                        disabled={isSaving}
                      />
                    ) : (
                      <p className="text-base font-gc-reg text-foreground">
                        {agent?.description || "No description provided"}
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm text-muted-foreground font-gc-reg">
                        Base URL
                      </p>
                      {!isEditing && (
                        <Tooltip content="Default base URL for all API actions. Individual actions can override this URL if needed. Leave empty to use full URLs in action configurations.">
                          <Info className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
                        </Tooltip>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-1">
                        <Input
                          type="url"
                          value={editedBaseUrl}
                          onChange={(e) => handleBaseUrlChange(e.target.value)}
                          placeholder="https://api.example.com"
                          className="text-base font-gc-reg"
                          disabled={isSaving}
                        />
                        {baseUrlError && (
                          <p className="text-sm text-red-500">{baseUrlError}</p>
                        )}
                      </div>
                    ) : (
                      <div className="text-base font-gc-reg">
                        {(agent?.agent_config as any)?.base_url ? (
                          <span className="text-maitai-green font-gc-med bg-maitai-green/10">
                            {(agent?.agent_config as any)?.base_url}
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">
                            Not configured
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Strategy Notes moved to Instructions tab (Special Instructions) */}
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                      Application
                    </p>
                    <button
                      onClick={() =>
                        navigate(`/application/${agent?.application_id}`)
                      }
                      className="text-base font-gc-med text-maitai-green hover:underline">
                      {agent?.application_name || application?.application_name}
                    </button>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                      Date Created
                    </p>
                    <p className="text-base font-gc-reg text-foreground">
                      {agent?.date_created
                        ? moment(agent.date_created).format(
                            "MMMM D, YYYY h:mm A"
                          )
                        : "Unknown"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Get Started Card - Only show if no capabilities */}
          {!capabilitiesLoading && !hasCapabilities && !isVersionView && (
            <Card className="border-border">
              <CardContent className="p-6">
                <h3 className="text-lg font-gc-med text-foreground mb-4">
                  Get Started
                </h3>
                <div className="text-center py-8">
                  <div className="space-y-4">
                    <SettingsIcon className="w-12 h-12 text-muted-foreground mx-auto" />
                    <div className="space-y-2">
                      <h4 className="text-base font-gc-med text-foreground">
                        Add Your First Action
                      </h4>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        Get started by adding capabilities to your agent.
                        Actions allow your agent to call APIs or LLMs, or
                        delegate to other agents.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        onClick={() => ensureDraftBeforeAction(() => setAddCapabilityOpen(true))}
                        variant="outline"
                        className="gap-2 border-maitai-green text-maitai-green hover:bg-maitai-green hover:text-white"
                        size="lg"
                        disabled={isDraftingFromVersion}>
                        <PlusIcon className="w-4 h-4" />
                        Add Action
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Request Activity Chart - Only show after capabilities loading is complete */}
          {!capabilitiesLoading && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <RequestAnalyticsChart
                  data={requestAnalyticsData}
                  loading={requestAnalyticsLoading}
                  title="Request Activity"
                  onQuickPeriodSelect={handleRequestAnalyticsPeriodSelect}
                  selectedPeriod={requestAnalyticsPeriod}
                  periodDays={requestAnalyticsPeriodDays}
                  className="h-full"
                />
              </div>

              {/* Live Activity Feed */}
              <div className="lg:col-span-1">
                <AgentLiveActivity
                  applicationId={application?.id}
                  agentId={Number(agentId)}
                />
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "capabilities",
      label: "Capabilities",
      icon: <SettingsIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">
                Agent Capabilities
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage actions and sub-agents that define what your agent can do
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate(`/agent/${agentId}/bulk-import`)}
                variant="outline"
                className="gap-2">
                <UploadIcon className="w-4 h-4" />
                Bulk Import
              </Button>
              <Button
                onClick={() => ensureDraftBeforeAction(() => setAddCapabilityOpen(true))}
                className="gap-2"
                disabled={isDraftingFromVersion}>
                <PlusIcon className="w-4 h-4" />
                Add Capability
              </Button>
            </div>
          </div>

          {/* Sub-tabs for table and graph view */}
          <div className="border-b border-border">
            <nav className="flex space-x-8">
              <button
                onClick={() => setCapabilitiesSubTab("table")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  capabilitiesSubTab === "table"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Table
              </button>
              <button
                onClick={() => setCapabilitiesSubTab("graph")}
                className={cn(
                  "px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                  capabilitiesSubTab === "graph"
                    ? "bg-maitai-green/10 text-maitai-green"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )}>
                Graph
              </button>
            </nav>
          </div>

          {/* Sub-tab content */}
          {capabilitiesSubTab === "table" ? (
            <AgentCapabilitiesTable
              key={`${agentId}-${capabilitiesRefreshKey}`}
              agentId={Number(agentId)}
              onAddCapability={() => setAddCapabilityOpen(true)}
              agent={agent}
              isVersionView={isVersionView}
              version={version}
              onVersionUpdated={() => { reloadAgent(); reloadVersionStatus(); }}
            />
          ) : // Conditionally render card wrapper only when there are capabilities
          !capabilitiesLoading && hasCapabilities ? (
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-base font-gc-med text-foreground">
                    Capabilities Diagram
                  </h4>
                  <div className="flex items-center gap-3">
                    {hasExpandedChildren && (
                      <Button
                        onClick={() => {
                          if (closeAllChildrenRef.current) {
                            closeAllChildrenRef.current();
                          }
                        }}
                        variant="ghost"
                        size="sm"
                        className="gap-2 text-sm">
                        Close Children
                      </Button>
                    )}
                  </div>
                </div>
                <AgentCapabilitiesGraph
                  key={`graph-${agentId}-${capabilitiesRefreshKey}`}
                  agentId={Number(agentId)}
                  agentName={agent?.agent_name || "Agent"}
                  onAddCapability={() => setAddCapabilityOpen(true)}
                  closeAllChildrenRef={closeAllChildrenRef}
                  onExpandedStateChange={setHasExpandedChildren}
                />
              </CardContent>
            </Card>
          ) : (
            // No card wrapper for empty state or loading
            <AgentCapabilitiesGraph
              key={`graph-${agentId}-${capabilitiesRefreshKey}`}
              agentId={Number(agentId)}
              agentName={agent?.agent_name || "Agent"}
              onAddCapability={() => setAddCapabilityOpen(true)}
              closeAllChildrenRef={closeAllChildrenRef}
              onExpandedStateChange={setHasExpandedChildren}
            />
          )}
        </div>
      ),
    },
    {
      key: "sessions",
      label: "Sessions",
      icon: <HistoryIcon className="w-4 h-4" />,
      content: <AgentSessionsTable agentId={Number(agentId)} />
    },
    {
      key: "versions",
      label: "Versions",
      icon: <FileText className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Agent Versions & Releases</h3>
              <p className="text-sm text-muted-foreground">
                View and manage published versions and releases
              </p>
            </div>
            {!isVersionView && (
              <div className="flex items-center gap-2">
                <VersionBadge 
                  version={latestVersion}
                  hasUnpublishedChanges={hasUnpublishedChanges}
                />
              </div>
            )}
          </div>
          
          <AgentVersionsTable 
            agentId={Number(agentId)}
            apiKey={apiKey}
            isVersionView={isVersionView}
          />
        </div>
      )
    }
  ];

  if (loadingState === "loading") {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              {/* Breadcrumb skeleton */}
              <nav className="mb-6">
                <div className="flex items-center gap-2">
                  <div className="h-4 bg-muted rounded w-12 animate-pulse" />
                  <span className="text-muted-foreground/40">/</span>
                  <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                  <span className="text-muted-foreground/40">/</span>
                  <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                  <span className="text-muted-foreground/40">/</span>
                  <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                </div>
              </nav>

              {/* Page header skeleton */}
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-80 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Tabs Card Loading */}
          <Card className="border-border">
            <CardContent className="p-0">
              {/* Tab navigation skeleton */}
              <div className="border-b border-border">
                <nav className="flex space-x-8 px-8 py-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="h-4 w-4 bg-muted rounded animate-pulse" />
                      <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                    </div>
                  ))}
                </nav>
              </div>

              {/* Tab content skeleton */}
              <div className="p-8 space-y-8">
                {/* Agent Details Card Skeleton */}
                <Card className="border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-6 bg-muted rounded w-32 animate-pulse" />
                      <div className="h-8 bg-muted rounded w-16 animate-pulse" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="space-y-2">
                            <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                            <div className="h-5 bg-muted rounded w-full animate-pulse" />
                          </div>
                        ))}
                      </div>
                      <div className="space-y-4">
                        {[...Array(2)].map((_, i) => (
                          <div key={i} className="space-y-2">
                            <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                            <div className="h-5 bg-muted rounded w-3/4 animate-pulse" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Request Activity Chart Skeleton */}
                <Card className="border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-6 bg-muted rounded w-36 animate-pulse" />
                      <div className="flex gap-2">
                        {[...Array(4)].map((_, i) => (
                          <div
                            key={i}
                            className="h-8 bg-muted rounded w-12 animate-pulse"
                          />
                        ))}
                      </div>
                    </div>
                    <div className="h-80 bg-muted rounded animate-pulse" />
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button
                    onClick={() => navigate("/forge/agents")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Agents
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                {/* Show breadcrumb chain if navigated from parent agent(s) */}
                {breadcrumbsLoading && parentsChain
                  ? // Show loading skeletons for breadcrumbs
                    parentsChain
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .map((_, index) => (
                        <React.Fragment key={`loading-${index}`}>
                          <li>
                            <Skeleton className="h-4 w-20 inline-block" />
                          </li>
                          <li className="text-muted-foreground/40">
                            <span>/</span>
                          </li>
                        </React.Fragment>
                      ))
                  : breadcrumbAgents.map((breadcrumbAgent, index) => (
                      <React.Fragment key={breadcrumbAgent.id}>
                        <li>
                          <button
                            onClick={() => {
                              // Navigate back to this agent with the appropriate parent chain
                              // Find the position of this agent's ID in the original parentsChain
                              const parentIds = parentsChain
                                ? parentsChain
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                : [];
                              const agentIdStr = breadcrumbAgent.id.toString();
                              const agentPositionInChain =
                                parentIds.indexOf(agentIdStr);

                              const chainUpToHere =
                                agentPositionInChain > 0
                                  ? parentIds
                                      .slice(0, agentPositionInChain)
                                      .join(",")
                                  : "";
                              const url = chainUpToHere
                                ? `/agent/${breadcrumbAgent.id}?parents=${chainUpToHere}`
                                : `/agent/${breadcrumbAgent.id}`;
                              navigate(url);
                            }}
                            className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                            {breadcrumbAgent.agent_name}
                          </button>
                        </li>
                        <li className="text-muted-foreground/40">
                          <span>/</span>
                        </li>
                      </React.Fragment>
                    ))}
                <li>
                  <span className="text-maitai-green font-gc-med">
                    {agent?.agent_name}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  {agent?.agent_name}
                </h1>
                {!isVersionView && (
                  <div className="flex items-center gap-2">
                    <VersionBadge 
                      version={latestVersion}
                      hasUnpublishedChanges={hasUnpublishedChanges}
                    />
                  </div>
                )}
                {isVersionView && version && (
                  <VersionBadge 
                    version={version}
                    hasUnpublishedChanges={false}
                  />
                )}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Manage agent capabilities, configuration, and performance
                </p>
                {!isVersionView && (hasUnpublishedChanges || !latestVersion) && (
                  <Button
                    onClick={() => setPublishModalOpen(true)}
                    size="sm"
                    className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Publish Version
                  </Button>
                )}
                {/* Show button when badge shows a version number (either viewing version OR draft with latestVersion) */}
                {(isVersionView && version) || (!isVersionView && latestVersion && !hasUnpublishedChanges) ? (
                  <Button
                    onClick={() => {
                      const versionToDraft = isVersionView ? version : latestVersion;
                      if (versionToDraft) {
                        handleDraftFromVersion(versionToDraft);
                      }
                    }}
                    size="sm"
                    disabled={isDraftingFromVersion}
                    className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white"
                  >
                    {isDraftingFromVersion ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      isVersionView ? "Draft From This Version" : `Draft From v${latestVersion}`
                    )}
                  </Button>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-8 py-4">
                {[
                  "overview",
                  "configuration",
                  "capabilities",
                  "sessions",
                  "versions",
                ].map((key) => {
                  const tab = tabs.find((t) => t.key === key);
                  if (!tab) return null;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                        activeTab === tab.key
                          ? "bg-maitai-green/10 text-maitai-green"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                      )}>
                      {tab.icon}
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {tabs.find((tab) => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>

        {/* Add Capability Modal */}
        <AddCapabilityModal
          isOpen={addCapabilityOpen}
          onClose={() => setAddCapabilityOpen(false)}
          agentId={Number(agentId)}
          currentAgent={agent}
          onCapabilityAdded={() => {
            setCapabilitiesRefreshKey((k) => k + 1);
            setAddCapabilityOpen(false);
            reloadAgent();
          }}
        />

        {/* Agent Form Modal */}
        <Dialog
          open={agentFormModalOpen}
          onOpenChange={(isOpen) => {
            if (!isOpen) setAgentFormModalOpen(false);
          }}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Agent Form</DialogTitle>
              <DialogDescription>
                Define optional session variables your agent can collect. This
                is optional.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-end mb-2">
              <Button
                onClick={saveAgentForm}
                disabled={!formDirty || formSaving}
                className="gap-2"
                style={{
                  backgroundColor:
                    formDirty && !formSaving ? "#10b981" : undefined,
                  color: formDirty && !formSaving ? "white" : undefined,
                }}>
                <CheckIcon className="w-4 h-4" />
                {formSaving ? "Saving..." : "Save"}
              </Button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {formSaving ? (
                <div className="space-y-4">
                  {[...Array(Math.max(1, formFields.length))].map((_, idx) => (
                    <div
                      key={idx}
                      className="border border-border rounded-lg bg-card">
                      <div className="p-6 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className="h-3 bg-muted rounded w-16 mb-2 animate-pulse" />
                                <div className="h-10 bg-muted rounded animate-pulse" />
                              </div>
                              <div>
                                <div className="h-3 bg-muted rounded w-8 mb-2 animate-pulse" />
                                <div className="h-10 bg-muted rounded animate-pulse" />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className="h-3 bg-muted rounded w-16 mb-2 animate-pulse" />
                                <div className="h-10 bg-muted rounded animate-pulse" />
                              </div>
                              <div>
                                <div className="h-3 bg-muted rounded w-16 mb-2 animate-pulse" />
                                <div className="h-10 bg-muted rounded animate-pulse" />
                              </div>
                            </div>
                            <div>
                              <div className="h-3 bg-muted rounded w-20 mb-2 animate-pulse" />
                              <div className="h-10 bg-muted rounded animate-pulse" />
                            </div>
                            <div>
                              <div className="h-3 bg-muted rounded w-20 mb-2 animate-pulse" />
                              <div className="h-24 bg-muted rounded animate-pulse" />
                            </div>
                          </div>
                          <div className="mt-6">
                            <div className="h-8 w-8 bg-muted rounded animate-pulse" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-center pt-2">
                    <div className="h-8 w-32 bg-muted rounded animate-pulse" />
                  </div>
                </div>
              ) : formFields.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border rounded-lg bg-muted/20">
                  <div className="w-12 h-12 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                    <EditIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h4 className="text-base font-medium text-foreground mb-2">
                    No form fields defined
                  </h4>
                  <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                    Form fields help your agent track important information like
                    user preferences, requirements, or session data.
                  </p>
                  <Button
                    onClick={addEmptyFormField}
                    className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white">
                    <PlusIcon className="w-4 h-4" />
                    Add First Field
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {formFields.map((field, idx) => (
                    <div
                      key={idx}
                      className="border border-border rounded-lg bg-card shadow-sm hover:shadow-md transition-shadow">
                      <div className="p-6 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                                  Field Name
                                </label>
                                <Input
                                  value={field.name}
                                  onChange={(e) =>
                                    updateFormField(idx, "name", e.target.value)
                                  }
                                  placeholder="e.g., departure_city, budget, preferences"
                                  className="font-mono text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                                  Type
                                </label>
                                <Select
                                  value={field.type}
                                  onValueChange={(value) =>
                                    updateFormField(idx, "type", value)
                                  }>
                                  <SelectTrigger className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="string">
                                      String
                                    </SelectItem>
                                    <SelectItem value="number">
                                      Number
                                    </SelectItem>
                                    <SelectItem value="boolean">
                                      Boolean
                                    </SelectItem>
                                    <SelectItem value="date">Date</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                                  Read Scope
                                </label>
                                <Select
                                  value={field.readScope || "private"}
                                  onValueChange={(value) =>
                                    updateFormField(idx, "readScope", value)
                                  }>
                                  <SelectTrigger className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="private">
                                      Private
                                    </SelectItem>
                                    <SelectItem value="public">
                                      Public
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                                  Write Scope
                                </label>
                                <Select
                                  value={field.writeScope || "private"}
                                  onValueChange={(value) => {
                                    // If setting write to custom while read is private, make read public
                                    if (
                                      (value === "custom" ||
                                        value === "public") &&
                                      (field.readScope || "private") ===
                                        "private"
                                    ) {
                                      updateFormField(
                                        idx,
                                        "readScope",
                                        "public"
                                      );
                                    }
                                    updateFormField(idx, "writeScope", value);
                                  }}>
                                  <SelectTrigger className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="private">
                                      Private
                                    </SelectItem>
                                    <SelectItem value="public">
                                      Public
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div>
                              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                                Description
                              </label>
                              <Input
                                value={field.description || ""}
                                onChange={(e) =>
                                  updateFormField(
                                    idx,
                                    "description",
                                    e.target.value
                                  )
                                }
                                placeholder="Describe what this field represents and how the agent should use it"
                                className="text-sm"
                              />
                            </div>

                            <div>
                              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                                Update Rules
                              </label>
                              <Textarea
                                value={field.updateRules || ""}
                                onChange={(e) =>
                                  updateFormField(
                                    idx,
                                    "updateRules",
                                    e.target.value
                                  )
                                }
                                placeholder="Define when and how this field should be updated..."
                                className="text-sm"
                                rows={3}
                              />
                            </div>
                            {/* Read-only permissions preview for direct subagents */}
                            <div>
                              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                                Subagent Permissions
                              </label>
                              <div className="border rounded">
                                <div className="grid grid-cols-6 px-3 py-2 text-xs text-muted-foreground border-b">
                                  <div className="col-span-4">Subagent</div>
                                  <div className="col-span-1">Read</div>
                                  <div className="col-span-1">Write</div>
                                </div>
                                <div className="max-h-40 overflow-auto">
                                  {(agent?.sub_agents || []).length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-muted-foreground">
                                      No direct subagents
                                    </div>
                                  ) : (
                                    (agent?.sub_agents || []).map((sa: any) => {
                                      const canRead =
                                        field.readScope === "public" ||
                                        (field.readScope === "custom"
                                          ? (field.readAgents || []).includes(
                                              sa.id
                                            )
                                          : false);
                                      const canWrite =
                                        field.writeScope === "public" ||
                                        (field.writeScope === "custom"
                                          ? (field.writeAgents || []).includes(
                                              sa.id
                                            )
                                          : false);
                                      return (
                                        <div
                                          key={`inline-prev-${sa.id}`}
                                          className="grid grid-cols-6 px-3 py-2 text-sm">
                                          <div
                                            className="col-span-4 truncate"
                                            title={sa.agent_name}>
                                            {sa.agent_name}
                                          </div>
                                          <div
                                            className={
                                              canRead
                                                ? "text-green-600 col-span-1"
                                                : "text-muted-foreground col-span-1"
                                            }>
                                            {canRead ? "Yes" : "No"}
                                          </div>
                                          <div
                                            className={
                                              canRead && canWrite
                                                ? "text-green-600 col-span-1"
                                                : "text-muted-foreground col-span-1"
                                            }>
                                            {canRead && canWrite ? "Yes" : "No"}
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <Button
                            onClick={() => removeFormField(idx)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 mt-6">
                            <XIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-center pt-2">
                    <Button
                      onClick={addEmptyFormField}
                      variant="outline"
                      size="sm"
                      className="gap-2 border-dashed hover:border-solid">
                      <PlusIcon className="w-4 h-4" />
                      Add Another Field
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Special Instructions Modal */}
        <Dialog
          open={specialInstructionsModalOpen}
          onOpenChange={(isOpen) => {
            if (!isOpen) setSpecialInstructionsModalOpen(false);
          }}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Special Instructions</DialogTitle>
              <DialogDescription>
                Provide optional guidance or constraints for your agent.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-end mb-2">
              <Button
                onClick={() => {
                  if (!agentId || !agent || notesSaving) return;
                  setNotesSaving(true);
                  const joined =
                    computeStrategyAddendumFromNotes(specialInstructions);
                  const mergedAgentConfig: any = {
                    ...(((agent as any)?.agent_config as any) || {}),
                  };
                  if (joined) {
                    mergedAgentConfig.strategy_prompt_addendum = joined;
                  } else if ("strategy_prompt_addendum" in mergedAgentConfig) {
                    delete mergedAgentConfig.strategy_prompt_addendum;
                  }
                  const updates: any = { agent_config: mergedAgentConfig };
                  AgentService.updateAgent(
                    Number(agentId),
                    updates,
                    () => {},
                    apiKey
                  ).subscribe(
                    () => {
                      setNotesDirty(false);
                      setNotesSaving(false);
                      setSpecialInstructionsModalOpen(false);
                      reloadAgent();
                    },
                    (error: any) => {
                      console.error(
                        "Error saving special instructions:",
                        error
                      );
                      setNotesSaving(false);
                    }
                  );
                }}
                disabled={!notesDirty || notesSaving}
                className="gap-2"
                style={{
                  backgroundColor:
                    notesDirty && !notesSaving ? "#10b981" : undefined,
                  color: notesDirty && !notesSaving ? "white" : undefined,
                }}>
                <CheckIcon className="w-4 h-4" />
                {notesSaving ? "Saving..." : "Save"}
              </Button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {specialInstructions.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border rounded-lg bg-muted/20">
                  <div className="w-12 h-12 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                    <EditIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h4 className="text-base font-medium text-foreground mb-2">
                    No notes added
                  </h4>
                  <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                    Add guidance, constraints, or examples to influence your
                    agent's behavior.
                  </p>
                  <Button
                    onClick={() => {
                      setSpecialInstructions([""]);
                      setNotesDirty(true);
                    }}
                    className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white">
                    <PlusIcon className="w-4 h-4" />
                    Add First Note
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {specialInstructions.map((note, idx) => (
                    <div
                      key={idx}
                      className="border border-border rounded-lg bg-card shadow-sm hover:shadow-md transition-shadow">
                      <div className="p-6 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                              Note
                            </label>
                            <Textarea
                              value={note}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSpecialInstructions((prev) =>
                                  prev.map((n, i) => (i === idx ? val : n))
                                );
                                setNotesDirty(true);
                              }}
                              placeholder="Add guidance or constraints..."
                              rows={4}
                            />
                          </div>
                          <Button
                            onClick={() => {
                              setSpecialInstructions((prev) =>
                                prev.filter((_, i) => i !== idx)
                              );
                              setNotesDirty(true);
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 mt-6">
                            <XIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-center pt-2">
                    <Button
                      onClick={() => {
                        setSpecialInstructions((prev) => [...prev, ""]);
                        setNotesDirty(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="gap-2 border-dashed hover:border-solid">
                      <PlusIcon className="w-4 h-4" />
                      Add Another Note
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Form Field Modal */}
        <FormFieldModal
          isOpen={formFieldModalOpen}
          onClose={() => {
            setFormFieldModalOpen(false);
            setEditingFormFieldIndex(null);
          }}
          onSave={saveFormFieldFromModal}
          editingField={
            editingFormFieldIndex !== null
              ? formFields[editingFormFieldIndex]
              : null
          }
          subagents={(agent?.sub_agents || []).map((sa: any) => ({
            id: sa.id,
            agent_name: sa.agent_name,
          }))}
        />

        {/* Special Instruction Modal */}
        <Dialog
          open={instructionModalOpen}
          onOpenChange={(isOpen) => {
            if (!isOpen) setInstructionModalOpen(false);
          }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingInstructionIndex !== null
                  ? "Edit Instruction"
                  : "Add Instruction"}
              </DialogTitle>
              <DialogDescription>
                Provide guidance or constraints for your agent.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="instruction-text">Instruction</Label>
                <Textarea
                  id="instruction-text"
                  value={tempInstruction}
                  onChange={(e) => setTempInstruction(e.target.value)}
                  placeholder="Add guidance, constraints, or examples..."
                  rows={5}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setInstructionModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={saveInstructionFromModal}
                disabled={!tempInstruction.trim()}>
                {editingInstructionIndex !== null
                  ? "Save Changes"
                  : "Add Instruction"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Prompt Modals for Structured Instructions */}
        <PromptModal
          isOpen={orchestrationModalOpen}
          onClose={() => setOrchestrationModalOpen(false)}
          onSave={handleOrchestrationSave}
          title="Orchestration Instructions"
          description="Planner guidance: how to chain/choose capabilities and sequencing."
          initialValue={orchestrationInstructions}
          placeholder="How the orchestrator should select and chain capabilities..."
          isSaving={instructionsSaving}
        />

        <PromptModal
          isOpen={strategyModalOpen}
          onClose={() => setStrategyModalOpen(false)}
          onSave={handleStrategySave}
          title="Strategy Instructions"
          description="Strategy guidance: how to best build strategies for chaining capabilities and accomplishing the request."
          initialValue={strategyInstructions}
          placeholder="How to best build strategies for chaining capabilities and accomplishing the request..."
          isSaving={instructionsSaving}
        />

        <PromptModal
          isOpen={processingModalOpen}
          onClose={() => setProcessingModalOpen(false)}
          onSave={handleProcessingSave}
          title="Processing Instructions"
          description="Rules for updating agent context, summaries, and state after actions."
          initialValue={processingInstructions}
          placeholder="When/how to update session context, state, ..."
          isSaving={instructionsSaving}
        />

        {/* Publish Modal */}
        <Dialog open={publishModalOpen} onOpenChange={(isOpen) => { if (!isOpen) setPublishModalOpen(false); }}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Publish Agent Version</DialogTitle>
              <DialogDescription>
                Create a new immutable version of this agent. All capability versions will be resolved and pinned.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="version-increment">Version Increment</Label>
                  <Select value={publishIncrement} onValueChange={(value: "major" | "minor" | "patch") => setPublishIncrement(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="major">Major (breaking changes)</SelectItem>
                      <SelectItem value="minor">Minor (new features)</SelectItem>
                      <SelectItem value="patch">Patch (bug fixes)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="version-notes">Version Notes *</Label>
                  <Textarea
                    id="version-notes"
                    value={publishNotes}
                    onChange={(e) => setPublishNotes(e.target.value)}
                    placeholder="Describe what changed in this version..."
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-medium">Capability Resolution</h4>
                <div className="border rounded-lg p-4 bg-muted/20">
                  <div className="text-sm text-muted-foreground mb-2">
                    The following capabilities will be pinned to specific versions:
                  </div>
                  <div className="space-y-2">
                    {(() => {
                      const versionSelections = (agent?.agent_config as any)?.version_selections || {};
                      const actionSelections = versionSelections.actions || {};
                      const subAgentSelections = versionSelections.sub_agents || {};
                      
                      // Count explicit vs latest for actions
                      type CountType = { explicit: number; latest: number };
                      const actionCounts: CountType = Object.values(actionSelections).reduce<CountType>((acc, sel: any) => {
                        if (sel?.type === "explicit") {
                          acc.explicit++;
                        } else {
                          acc.latest++;
                        }
                        return acc;
                      }, { explicit: 0, latest: 0 });
                      
                      // Count explicit vs latest for sub-agents
                      const subAgentCounts: CountType = Object.values(subAgentSelections).reduce<CountType>((acc, sel: any) => {
                        if (sel?.type === "explicit") {
                          acc.explicit++;
                        } else {
                          acc.latest++;
                        }
                        return acc;
                      }, { explicit: 0, latest: 0 });
                      
                      // Get explicit version numbers for display
                      const getExplicitVersions = (selections: any, count: number): string[] | null => {
                        if (count === 0) return null;
                        const explicit = Object.entries(selections)
                          .filter(([_, sel]: [string, any]) => sel?.type === "explicit")
                          .map(([_, sel]: [string, any]) => sel.version_number)
                          .filter(Boolean)
                          .slice(0, 3); // Show up to 3 versions
                        return explicit.length > 0 ? explicit : null;
                      };
                      
                      const explicitActionVersions = getExplicitVersions(actionSelections, actionCounts.explicit);
                      const explicitSubAgentVersions = getExplicitVersions(subAgentSelections, subAgentCounts.explicit);
                      
                      const totalActions = (agent?.actions || []).length;
                      const totalSubAgents = (agent?.sub_agents || []).length;
                      
                      return (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span>Agent Actions {totalActions > 0 && `(${totalActions})`}</span>
                            <span className="text-muted-foreground">
                              {actionCounts.explicit > 0 && actionCounts.latest > 0 ? (
                                <>
                                  {explicitActionVersions && (
                                    <span className="font-medium">{explicitActionVersions.join(", ")}{explicitActionVersions.length < actionCounts.explicit ? "..." : ""}</span>
                                  )}
                                  {actionCounts.explicit > 0 && actionCounts.latest > 0 && " + "}
                                  {actionCounts.latest > 0 && `${actionCounts.latest} latest`}
                                </>
                              ) : actionCounts.explicit > 0 ? (
                                <>
                                  {explicitActionVersions && (
                                    <span className="font-medium">{explicitActionVersions.join(", ")}{explicitActionVersions.length < actionCounts.explicit ? "..." : ""}</span>
                                  )}
                                  {actionCounts.explicit === 1 ? " version" : " versions"}
                                </>
                              ) : (
                                totalActions > 0 ? "Latest versions will be resolved" : "No actions"
                              )}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span>Sub-agents {totalSubAgents > 0 && `(${totalSubAgents})`}</span>
                            <span className="text-muted-foreground">
                              {subAgentCounts.explicit > 0 && subAgentCounts.latest > 0 ? (
                                <>
                                  {explicitSubAgentVersions && (
                                    <span className="font-medium">{explicitSubAgentVersions.join(", ")}{explicitSubAgentVersions.length < subAgentCounts.explicit ? "..." : ""}</span>
                                  )}
                                  {subAgentCounts.explicit > 0 && subAgentCounts.latest > 0 && " + "}
                                  {subAgentCounts.latest > 0 && `${subAgentCounts.latest} latest`}
                                </>
                              ) : subAgentCounts.explicit > 0 ? (
                                <>
                                  {explicitSubAgentVersions && (
                                    <span className="font-medium">{explicitSubAgentVersions.join(", ")}{explicitSubAgentVersions.length < subAgentCounts.explicit ? "..." : ""}</span>
                                  )}
                                  {subAgentCounts.explicit === 1 ? " version" : " versions"}
                                </>
                              ) : (
                                totalSubAgents > 0 ? "Latest versions will be resolved" : "No sub-agents"
                              )}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPublishModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  if (!publishNotes.trim()) return;
                  setIsPublishing(true);
                  try {
                    const versionResult = await AgentService.publishAgentVersion(
                      Number(agentId),
                      publishIncrement,
                      publishNotes.trim(),
                      () => {},
                      apiKey
                    ).toPromise();
                    setPublishModalOpen(false);
                    setIsPublishing(false);
                    
                    // Extract version number from response
                    const versionNumber = versionResult?.version_number;
                    
                    // Wait a moment for backend to finish updating agent row (removing draft metadata, etc.)
                    setTimeout(() => {
                      // Navigate to version view if we got a version number
                      if (versionNumber) {
                        navigate(`/agent/${agentId}?version=${versionNumber}`, { replace: true });
                      } else {
                        // Fallback: reload agent and version status
                      reloadAgent();
                      reloadVersionStatus();
                      }
                    }, 500); // Small delay to ensure backend has finished updating
                  } catch (err) {
                    console.error("Error publishing version:", err);
                    toast.error("Failed to publish version");
                    setIsPublishing(false);
                  }
                }}
                disabled={!publishNotes.trim() || isPublishing}
                className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white"
              >
                {isPublishing ? "Publishing..." : "Publish Version"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Draft From Version Confirmation Dialog */}
        <AlertDialog 
          open={draftConfirmDialogOpen} 
          onOpenChange={(open) => {
            if (!open) {
              setDraftConfirmDialogOpen(false);
              setPendingDraftVersion(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-gc-med">
                Create Draft from Version
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to create a draft from version {pendingDraftVersion}? This will overwrite the current draft.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDraftFromVersion}
                className="bg-maitai-green hover:bg-maitai-green/90 text-white"
              >
                Create Draft
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Save Changes Confirmation Dialog (when viewing a version) */}
        <AlertDialog 
          open={saveConfirmDialogOpen} 
          onOpenChange={(open) => {
            // Prevent closing while creating draft
            if (!open && !isDraftingFromVersion) {
              setSaveConfirmDialogOpen(false);
              setPendingSaveAction(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-gc-med">
                Save Changes to Draft
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isDraftingFromVersion ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Creating draft and applying changes...</span>
                  </div>
                ) : (
                  `You are viewing version ${version}. Saving this change will create a new draft from this version and apply your changes to it. Do you want to continue?`
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                onClick={() => {
                setSaveConfirmDialogOpen(false);
                setPendingSaveAction(null);
                }}
                disabled={isDraftingFromVersion}
              >
                Go Back
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmSaveWithDraft}
                disabled={isDraftingFromVersion}
                className="bg-maitai-green hover:bg-maitai-green/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDraftingFromVersion ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Creating Draft...</span>
                  </div>
                ) : (
                  "Save to Draft"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Field Move Confirmation Dialog */}
        <AlertDialog open={fieldMoveDialogOpen} onOpenChange={setFieldMoveDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Move field{pendingFieldAction?.fieldNames && pendingFieldAction.fieldNames.length > 1 ? "s" : ""}?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingFieldAction?.type === "bulkMove" && pendingFieldAction.fieldNames ? (
                  `Move ${pendingFieldAction.fieldNames.length} field${pendingFieldAction.fieldNames.length > 1 ? "s" : ""} (${pendingFieldAction.fieldNames.join(", ")}) to ${pendingFieldAction.targetAgentName}?`
                ) : pendingFieldAction?.type === "move" && pendingFieldAction.fieldNames ? (
                  `Move field "${pendingFieldAction.fieldNames[0]}" to ${pendingFieldAction.targetAgentName}?`
                ) : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setPendingFieldAction(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (pendingFieldAction?.type === "bulkMove") {
                    executeBulkMove();
                  } else if (pendingFieldAction?.type === "move") {
                    executeSingleMove();
                  }
                }}
                className="bg-maitai-green hover:bg-maitai-green/90 text-white"
              >
                Move
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Field Delete Confirmation Dialog */}
        <AlertDialog open={fieldDeleteDialogOpen} onOpenChange={setFieldDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete field{pendingFieldAction?.fieldNames && pendingFieldAction.fieldNames.length > 1 ? "s" : ""}?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingFieldAction?.fieldNames ? (
                  `Delete ${pendingFieldAction.fieldNames.length} field${pendingFieldAction.fieldNames.length > 1 ? "s" : ""}: ${pendingFieldAction.fieldNames.join(", ")}? This action cannot be undone.`
                ) : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setPendingFieldAction(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={executeBulkDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>
    </div>
  );
};

export default AgentOverview;
